import type { Quote } from '@client/model/quotesModel';
import { fundingPolicyTypeDefs } from '@shared/FundingPolicyTypeDef';
import { FundingPolicyType } from '@shared/appModel';
import { getPaymentOptionFromQuote } from './quotePaymentOption';

const DEATH_BENEFIT_YES_POLICY_TYPES: FundingPolicyType[] = [
  FundingPolicyType.lifeInsuranceWithRider,
  FundingPolicyType.hybridLifeInsurance,
  FundingPolicyType.jointHybridAssetBased,
  FundingPolicyType.annuityHybrid,
  FundingPolicyType.jointHybridAnnuityBased,
];

const DEATH_BENEFIT_NO_POLICY_TYPES: FundingPolicyType[] = [
  FundingPolicyType.longTermCareInsurance,
  FundingPolicyType.jointLongTermCareInsurance,
  FundingPolicyType.shortTermCareInsurance,
  // ManhattanLife OmniFlex STC has no death benefit; its calculationType is
  // itself, so without this entry a "no death benefit" preference would
  // silently filter its quotes out.
  FundingPolicyType.manhattanLifeOmniFlexStc,
];

const PAYMENT_STRUCTURE_TO_OPTIONS: Record<
  LtcFinancialPlanningPaymentStructure,
  LtcFinancialPlanningPaymentOption[]
> = {
  upfront: ['Single Pay'],
  monthly: ['Monthly'],
  balanced: ['5-Pay', '10-Pay', 'Annually'],
  noPreference: ['Single Pay', '5-Pay', '10-Pay', 'Monthly', 'Annually'],
};

export function determinePolicyTypesFromPreferences(
  preferences: PolicyPreferences,
): FundingPolicyType[] {
  const { deathBenefit } = preferences;

  if (deathBenefit === 'yes') {
    return DEATH_BENEFIT_YES_POLICY_TYPES;
  }

  if (deathBenefit === 'no') {
    return DEATH_BENEFIT_NO_POLICY_TYPES;
  }

  return [...DEATH_BENEFIT_YES_POLICY_TYPES, ...DEATH_BENEFIT_NO_POLICY_TYPES];
}

export function filterQuotesByPreferences(
  quotes: Quote[],
  preferences: PolicyPreferences,
): Quote[] {
  const { deathBenefit, paymentStructure, benefitStructure } = preferences;

  let filtered = [...quotes];

  if (deathBenefit) {
    const allowedPolicyTypes = determinePolicyTypesFromPreferences(preferences);
    filtered = filtered.filter(quote => {
      const policyDef = fundingPolicyTypeDefs[quote.fundingPolicyType];
      if (!policyDef) return true;
      return allowedPolicyTypes.includes(policyDef.calculationType);
    });
  }

  if (paymentStructure && paymentStructure !== 'noPreference') {
    const allowedPaymentOptions = PAYMENT_STRUCTURE_TO_OPTIONS[paymentStructure];
    filtered = filtered.filter(quote => {
      const quotePaymentOption = getPaymentOptionFromQuote(quote);
      return allowedPaymentOptions.includes(quotePaymentOption);
    });
  }

  if (benefitStructure === 'guaranteed') {
    filtered = filtered.filter(quote => {
      const hasReturnOfPremium =
        quote.returnOfPremiumPercent && parseFloat(quote.returnOfPremiumPercent) > 0;
      const hasGuaranteedDeathBenefit =
        (quote.deathBenefitMinimumGuaranteedIndividual ?? 0) > 0 ||
        (quote.deathBenefitMinimumGuaranteedJoint ?? 0) > 0;
      const policyDef = fundingPolicyTypeDefs[quote.fundingPolicyType];
      const isAnnuityBased = policyDef?.calculationType === FundingPolicyType.annuityHybrid;

      return hasReturnOfPremium || hasGuaranteedDeathBenefit || isAnnuityBased;
    });
  }

  return filtered.sort((a, b) => (b.roi ?? 0) - (a.roi ?? 0));
}

export function hasMatchingQuotes(quotes: Quote[], preferences: PolicyPreferences): boolean {
  return filterQuotesByPreferences(quotes, preferences).length > 0;
}
